import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dna, FlaskConical, Brain, BookOpenText, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

export default function MendelLanding() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-cream" data-testid="mendel-landing">
      {/* Hero */}
      <section className="container-narrow pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-3 inline-flex items-center gap-2 text-marigold">
              <Dna className="w-3 h-3" /> {t("mendel.eyebrow")}
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-semibold leading-[1.02] mb-5">
              {t("mendel.title1")} <span className="italic text-marigold">{t("mendel.title2")}</span>{t("mendel.title3")}
            </h1>
            <p className="text-lg text-ink2 leading-relaxed max-w-xl mb-8">
              {t("mendel.body")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/mendel" className="btn-primary" data-testid="mendel-landing-open-ide">
                {t("mendel.cta_ide")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/mendel/lessons/what-is-biological-programming" className="btn-outline-ink" data-testid="mendel-landing-start-lessons">
                {t("mendel.cta_lessons")}
              </Link>
            </div>
          </div>

          {/* Karyogram-ish preview */}
          <div className="lg:col-span-5">
            <div className="card-soft p-6 shadow-[6px_6px_0_0_#1A1B41]" data-testid="mendel-hero-karyogram">
              <div className="label-eyebrow mb-3">Live · Chromosome 11</div>
              <div className="font-mono text-xs text-ink2 mb-2">GRCh38 · 135.1 Mb</div>
              <div className="relative h-6 rounded-full bg-ink/10 overflow-hidden">
                <div className="absolute left-[39%] top-0 bottom-0 w-1 bg-ink2/40" title="centromere" />
                <div className="absolute top-1/2 -translate-y-1/2 left-[4%] w-1.5 h-1.5 rounded-full bg-marigold" title="HBB · 5.2 Mb" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold">Featured gene</div>
                  <div className="font-hand text-2xl text-marigold">HBB</div>
                  <div className="text-ink2 text-xs">Hemoglobin subunit β</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold">Textbook variant</div>
                  <div className="font-mono text-xs">c.20A&gt;T · p.Glu7Val</div>
                  <div className="text-ink2 text-xs">Sickle-cell</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="container-narrow pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mendel-feature-grid">
          <FeatureCard icon={<Dna className="w-4 h-4" />} title="Genome IDE"
            body="A VS Code-shaped workbench for biology. Project explorer, tabbed workspaces, biological console." />
          <FeatureCard icon={<FlaskConical className="w-4 h-4" />} title="Chromosome Explorer"
            body="Scale-accurate human karyogram. Click any tick to jump to a gene detail page." />
          <FeatureCard icon={<Sparkles className="w-4 h-4" />} title="Mendelian Simulator"
            body="Compute Punnett squares in one click. Genotype + phenotype probabilities, labelled as models." />
          <FeatureCard icon={<Brain className="w-4 h-4" />} title="Mendel AI"
            body="An OpenAI GPT-5.4 research companion that reads your active workspace and cites what it knows." />
          <FeatureCard icon={<BookOpenText className="w-4 h-4" />} title="Ms. Pradhan Lessons"
            body="A 20-lesson computational-genetics curriculum, from base pairs to population dynamics." />
          <FeatureCard icon={<ShieldCheck className="w-4 h-4" />} title="Safety-First"
            body="Educational and research computational biology only. No operational wet-lab or medical instructions." />
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="card-soft p-5" data-testid={`mendel-feature-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-full border-2 border-ink bg-marigold/20 inline-flex items-center justify-center text-marigold">{icon}</span>
        <span className="font-heading text-lg font-semibold">{title}</span>
      </div>
      <p className="text-ink2 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
